import { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import api, { fileUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { toast } from "sonner";
import { Loader2, Camera } from "lucide-react";

export default function Profile() {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [bio, setBio] = useState(user?.bio || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [picture, setPicture] = useState(user?.picture || "");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  const onAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/image", fd);
      setPicture(data.path);
      toast.success("Photo mise à jour");
    } catch {
      toast.error("Échec");
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.patch("/auth/me", { name, bio, picture, phone: phone || null });
      setUser(data);
      toast.success("Profil mis à jour");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="profile-page">
      <div className="mb-8">
        <p className="text-sm text-muted-foreground mb-1">Profil</p>
        <h1 className="font-display text-4xl font-bold tracking-tight">Mon profil</h1>
      </div>

      <form onSubmit={submit} className="card-soft p-8 space-y-6">
        <div className="flex items-center gap-6">
          <div className="relative">
            <Avatar className="h-24 w-24">
              <AvatarImage src={picture ? fileUrl(picture) : undefined} />
              <AvatarFallback className="gradient-gold-green text-white text-2xl">{user?.name?.[0]?.toUpperCase()}</AvatarFallback>
            </Avatar>
            <label className="absolute bottom-0 right-0 h-9 w-9 rounded-full gradient-gold-green flex items-center justify-center cursor-pointer shadow-lg" data-testid="upload-avatar-label">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin text-white" /> : <Camera className="h-4 w-4 text-white" />}
              <input type="file" accept="image/*" onChange={onAvatarUpload} className="hidden" disabled={uploading} data-testid="upload-avatar-input" />
            </label>
          </div>
          <div>
            <h3 className="font-display font-semibold text-xl">{user?.name}</h3>
            <p className="text-sm text-muted-foreground">{user?.email}</p>
          </div>
        </div>

        <div>
          <Label htmlFor="name">Nom complet</Label>
          <Input id="name" value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11 rounded-xl" data-testid="profile-name" />
        </div>
        <div>
          <Label htmlFor="phone">Numéro de téléphone</Label>
          <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+221771787379" inputMode="tel" className="mt-1.5 h-11 rounded-xl" data-testid="profile-phone" />
          <p className="text-xs text-muted-foreground mt-1">Formats acceptés : 77XXXXXXX, +221XXXXXXXXX ou 00221XXXXXXXXX</p>
        </div>
        <div>
          <Label htmlFor="bio">Bio</Label>
          <Textarea id="bio" rows={4} value={bio} onChange={(e) => setBio(e.target.value)} placeholder="Parlez de vous..." className="mt-1.5 rounded-xl" data-testid="profile-bio" />
        </div>

        <Button type="submit" disabled={loading} className="btn-gradient rounded-full h-11 px-8" data-testid="profile-submit">
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enregistrer"}
        </Button>
      </form>
    </div>
  );
}
